import { Box, Flex, Image } from "@chakra-ui/react";
import { useField } from "formik";
import { LexicalEditor } from "lexical";
import React from "react";
import { FormHelpPopover } from "src/components/FormHelpPopover";
import { EditorRef } from "src/components/WysiwygEditor/lexical/Editor";
import FormikLexicalEditor, {
  Props as FormikLexicalEditorProps,
} from "src/components/WysiwygEditor/lexical/FormikEditor";
import { useContentConversion } from "src/components/WysiwygEditor/lexical/hooks/useContentConversion";
import NextStepBody from "src/images/next-step-body.svg";
import NextStepButtonText from "src/images/next-step-button-text.svg";
import NextStepSubtitle from "src/images/next-step-subtitle.svg";
import NextStepTitle from "src/images/next-step-title.svg";
import { MaxCharacter } from "./legacyRules";

export const NextStepForm: React.FC = () => {
  return (
    <Flex direction="column" gap="4">
      <FormikNextStepLexicalEditor
        maxLength={MaxCharacter.title}
        key="title"
        name="title"
        label={
          <Flex alignItems="center" gap="2">
            <Box>Title</Box>
            <FormHelpPopover
              title="Celebrate what's next"
              image={
                <Image
                  src={NextStepTitle}
                  paddingX={16}
                  paddingTop={2}
                  bg="gray.100"
                />
              }
            >
              Delight families by celebrating milestones and introducing them to
              what's next
            </FormHelpPopover>
          </Flex>
        }
        placeholder="E.g.: {{student.name}} can now continue!"
        height="40px"
      />
      <FormikNextStepLexicalEditor
        maxLength={MaxCharacter.subtitle}
        key="subtitle"
        name="subtitle"
        label={
          <Flex alignItems="center" gap="2">
            <Box>Subtitle</Box>
            <FormHelpPopover
              title="Introduce what's next"
              image={
                <Image
                  src={NextStepSubtitle}
                  paddingX={16}
                  paddingTop={2}
                  bg="gray.100"
                />
              }
            >
              Grab families' attention and introduce what's next with a short
              and concise subtitle
            </FormHelpPopover>
          </Flex>
        }
        placeholder="E.g: {{student.firstName}} can register at {{offer.schoolName}} now."
        height="80px"
      />
      <FormikNextStepLexicalEditor
        maxLength={MaxCharacter.body}
        key="body"
        name="body"
        label={
          <Flex alignItems="center" gap="2">
            <Box>Text to display when continuing</Box>
            <FormHelpPopover
              title="Welcome families to what's next"
              image={
                <Image
                  src={NextStepBody}
                  paddingX={16}
                  paddingTop={2}
                  bg="gray.100"
                />
              }
            >
              Ensure they know what to do and what to expect with a friendly
              message.
            </FormHelpPopover>
          </Flex>
        }
        placeholder="E.g: Thank you for accepting your offer! An option to register will show up shortly"
        height="150px"
      />
      <FormikNextStepLexicalEditor
        maxLength={MaxCharacter.buttonText}
        key="buttonText"
        name="buttonText"
        label={
          <Flex alignItems="center" gap="2">
            <Box>Text on the main button to continue</Box>
            <FormHelpPopover
              title="Guide families to continue"
              image={
                <Image
                  src={NextStepButtonText}
                  paddingX={8}
                  paddingTop={0}
                  bg="gray.100"
                />
              }
            >
              Help families with a concise call to action that clearly indicates
              how to continue.
            </FormHelpPopover>
          </Flex>
        }
        placeholder="E.g.: Continue here"
        height="40px"
      />
    </Flex>
  );
};

type NextStepProps = { maxLength: number } & FormikLexicalEditorProps;
const FormikNextStepLexicalEditor: React.FC<NextStepProps> = ({
  maxLength,
  name,
  ...props
}) => {
  const [editor, setEditor] = React.useState<LexicalEditor>();
  const titleEditorRef: EditorRef = React.useCallback(
    (editor: LexicalEditor) => {
      if (editor != null) setEditor(editor);
    },
    []
  );

  const converter = useContentConversion({
    variableMode: "expanded",
    enableVariables: true,
  });

  const [field, , helper] = useField(`nextStep.${name}`);
  const [fieldLexical, ,] = useField(`nextStep.${name}Lexical`);

  React.useEffect(() => {
    if (!editor) return;
    if (!field.value) return;
    if (fieldLexical.value) return;

    converter.fromPlainText(editor, field.value ?? "");
  }, [field.value, fieldLexical.value, editor, name, converter]);

  const [characterLeft, setCharacterLeft] = React.useState(maxLength);

  return (
    <Flex direction="column" gap="1">
      <FormikLexicalEditor
        name={`nextStep.${name}Lexical`}
        width="100%"
        editorVersion="lite"
        settings={{
          enableSmsStats: false,
          enableVariables: true,
          variablesContext: { type: "nextStep" },
          hideToolbar: true,
        }}
        editorRef={titleEditorRef}
        onFieldValueChange={(editorState) => {
          const plainText = converter.toPlainText(editor, editorState);
          helper.setValue(plainText, true);
          const charLeft = maxLength - plainText.length;
          setCharacterLeft(charLeft);
        }}
        helperText={`${characterLeft}/${maxLength}`}
        {...props}
      />
    </Flex>
  );
};
